import { WhipError } from './errors';

/**
 * One restream target. `provider` is matched by name downstream, where `youtube`, `twitch` and `substack` are understood
 * and anything else is silently dropped — the gateway forwards unknown names, the transcoder ignores them.
 */
export interface StreamKey {
	provider: 'youtube' | 'twitch' | 'substack' | (string & {});
	key: string;
}

/**
 * How this session identifies itself, which is also how it picks a route.
 *
 * The production endpoint (`/v2/rtc/whip`) takes a bearer JWT: Envoy validates it, strips it, and turns its claims into the
 * `x-auth-*` headers the gateway actually reads — including the stream keys, which is why they are not passed alongside it.
 *
 * The debug endpoint (`/v2/rtc/whip/debug`, disabled by default in production) has no bearer, so the client sends those
 * headers itself. Every field is optional there: the gateway substitutes a random UUID for an identity it was not given.
 */
export type WhipAuth = { bearerToken: string } | { livestreamId?: string; userId?: string; streamKeys?: StreamKey[] };

/**
 * Standard padded base64 of the UTF-8 bytes — which is what the gateway's decoder expects, and not what `btoa` alone
 * produces: `btoa` throws on any code point above U+00FF, so a key with a non-Latin1 character would fail here rather
 * than at the server.
 */
export function encodeStreamKeys(keys: StreamKey[]): string {
	const bytes = new TextEncoder().encode(JSON.stringify(keys));
	let latin1 = '';
	for (const byte of bytes) latin1 += String.fromCharCode(byte);
	return btoa(latin1);
}

/** The headers that carry identity, on whichever route the endpoint points at. Content-Type is the caller's to add. */
export function authHeaders(auth?: WhipAuth): Record<string, string> {
	if (!auth) return {};

	if ('bearerToken' in auth) {
		if (!auth.bearerToken) throw new WhipError('invalid-options', 'auth.bearerToken is empty');
		return { Authorization: `Bearer ${auth.bearerToken}` };
	}

	const headers: Record<string, string> = {};
	if (auth.livestreamId) headers['x-auth-livestream-id'] = auth.livestreamId;
	if (auth.userId) headers['x-auth-user-id'] = auth.userId;

	// A key is only a key with both halves. Sending a provider with an empty string would name that provider as a restream
	// target and then fail at it, so an incomplete pair is dropped here instead.
	const keys = (auth.streamKeys ?? []).filter((k) => k.provider && k.key);
	if (keys.length > 0) headers['x-auth-stream-keys'] = encodeStreamKeys(keys);

	return headers;
}
